'use client'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { IGetRestaurantsQueryParams } from '@/api/restaurants/get-restaurants-query'
import { IRestaurantCitySearchQuerySetters } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import {
    DEFAULT_RESTAURANT_SORTING,
    ICityUrlParams,
    RestaurantQuerySorting,
    RestaurantsCitySearchUrlParams,
} from '@/setup/urls/city-route'
import { useOptimisticSearchParamState } from '@/utils/url/use-optimistic-url-param-state'

// Permissions API is unavailable in some browsers, silently skip then
const noOpPermissionError = () => undefined

/**
 * Survives client-side navigations (e.g. restaurant page and back) so the
 * distance labels render immediately on remount. Deliberately module-scoped
 * memory only, never persisted.
 */
let cachedCoordinates: { lat: string; lng: string } | null = null

// A recent cached position is fine for city-level distances
const GEOLOCATION_MAX_AGE_MS = 5 * 60 * 1000

interface UseRestaurantCityManageSearchQueryReturn {
    query: Omit<IGetRestaurantsQueryParams, 'page'>
    setters: IRestaurantCitySearchQuerySetters
    /** In-memory user position, set once location access is granted */
    coordinates: { lat: string; lng: string } | null
}

/**
 * This hook manages the search query for the restaurant city search page.
 * It handles updated to the url, as well as the local state.
 *
 * If you want to add a new parameter to the search, add it here.
 */
export function useRestaurantCitySearchManager(): UseRestaurantCityManageSearchQueryReturn {
    const t = useTranslations()

    // Kept in memory only: coordinates are personal data and must not end up
    // in the shareable page url. They are sent to our api with the request.
    const [coordinates, rawSetCoordinates] = useState<{ lat: string; lng: string } | null>(
        cachedCoordinates
    )

    const setCoordinates = useCallback((next: { lat: string; lng: string }) => {
        cachedCoordinates = next
        rawSetCoordinates(next)
    }, [])

    const [rawSortBy, rawSetSortBy] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.SORT_BY,
        'search',
        'string',
        'replace'
    )

    const sortBy = useMemo(
        () =>
            Object.values(RestaurantQuerySorting).includes(rawSortBy as RestaurantQuerySorting)
                ? (rawSortBy as RestaurantQuerySorting)
                : DEFAULT_RESTAURANT_SORTING,
        [rawSortBy]
    )

    const [citySlug, rawSetCitySlug] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.CITY_SLUG,
        'path',
        'string',
        'push'
    )

    const [name, rawSetName] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.SEARCH,
        'search',
        'string',
        'replace'
    )

    const [rawOpenNow, rawSetOpenNow] = useOptimisticSearchParamState<string>(
        RestaurantsCitySearchUrlParams.OPEN_NOW,
        'search',
        'string',
        'replace'
    )

    const [tagSlugs, rawSetTagSlugs] = useOptimisticSearchParamState<string[]>(
        RestaurantsCitySearchUrlParams.TAGS,
        'search',
        'array',
        'replace'
    )

    const query = useMemo<ICityUrlParams>(
        () => ({
            citySlug: typeof citySlug === 'string' ? citySlug : '',
            name: name || undefined,
            tagSlugs: tagSlugs || undefined,
            // Nearest only works with coordinates, until they are available
            // (permission prompt, page reload) the default order is used
            sortBy:
                sortBy === RestaurantQuerySorting.NEAREST && !coordinates
                    ? DEFAULT_RESTAURANT_SORTING
                    : sortBy,
            lat: sortBy === RestaurantQuerySorting.NEAREST ? coordinates?.lat : undefined,
            lng: sortBy === RestaurantQuerySorting.NEAREST ? coordinates?.lng : undefined,
            openNow: rawOpenNow === 'true' || undefined,
        }),
        [citySlug, name, tagSlugs, sortBy, rawOpenNow, coordinates]
    )

    const setCitySlug = useCallback(
        (nextCitySlug: string) => {
            rawSetCitySlug(
                nextCitySlug,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, citySlug: nextCitySlug },
                },
                { scroll: false }
            )
        },
        [query, rawSetCitySlug]
    )

    const setName = useCallback(
        (nextName: string) => {
            rawSetName(
                nextName,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, name: nextName },
                },
                { scroll: false }
            )
        },

        [query, rawSetName]
    )

    const setTagSlugs = useCallback(
        (nextTagSlugs: string[]) => {
            rawSetTagSlugs(
                nextTagSlugs,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, tagSlugs: nextTagSlugs },
                },
                { scroll: false }
            )
        },
        [query, rawSetTagSlugs]
    )

    const applySortBy = useCallback(
        (nextSortBy: RestaurantQuerySorting) => {
            rawSetSortBy(
                nextSortBy,
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, sortBy: nextSortBy },
                },
                { scroll: false }
            )
        },
        [query, rawSetSortBy]
    )

    const setSortBy = useCallback(
        (nextSortBy: RestaurantQuerySorting) => {
            if (nextSortBy !== RestaurantQuerySorting.NEAREST || coordinates) {
                applySortBy(nextSortBy)

                return
            }

            if (typeof navigator === 'undefined' || !navigator.geolocation) return

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setCoordinates({
                        lat: `${position.coords.latitude}`,
                        lng: `${position.coords.longitude}`,
                    })

                    applySortBy(nextSortBy)
                },
                () => {
                    toast.error(t('restaurants.city.sorting.locationDenied'))
                },
                { maximumAge: GEOLOCATION_MAX_AGE_MS }
            )
        },
        [applySortBy, coordinates, setCoordinates, t]
    )

    // When location access was granted before, fetch the position silently on
    // mount: it powers distance labels on the cards and makes nearest deep
    // links work. Visitors without a prior grant never see a prompt here.
    useEffect(() => {
        if (coordinates) return
        if (typeof navigator === 'undefined' || !navigator.permissions) return

        let cancelled = false

        navigator.permissions
            .query({ name: 'geolocation' })
            .then((status) => {
                if (cancelled || status.state !== 'granted') return

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        if (cancelled) return

                        setCoordinates({
                            lat: `${position.coords.latitude}`,
                            lng: `${position.coords.longitude}`,
                        })
                    },
                    noOpPermissionError,
                    { maximumAge: GEOLOCATION_MAX_AGE_MS }
                )
            })
            .catch(noOpPermissionError)

        return () => {
            cancelled = true
        }
    }, [coordinates, setCoordinates])

    const setOpenNow = useCallback(
        (nextOpenNow: boolean) => {
            rawSetOpenNow(
                nextOpenNow ? 'true' : '',
                {
                    pattern: '/restaurants/[citySlug]',
                    params: { ...query, openNow: nextOpenNow },
                },
                { scroll: false }
            )
        },
        [query, rawSetOpenNow]
    )

    const setters = useMemo(
        () => ({
            setCitySlug,
            setName,
            setTagSlugs,
            setSortBy,
            setOpenNow,
        }),
        [setCitySlug, setName, setTagSlugs, setSortBy, setOpenNow]
    )

    return useMemo(
        () => ({
            query,
            setters,
            coordinates,
        }),
        [query, setters, coordinates]
    )
}
